import { InfiniteData, QueryClient, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { BoardApi } from "../../../services/board-service-api";
import { boardKeys } from "./board-query-keys";
import { BoardService } from "./board-service";
import { columnShouldReload } from "./change-event-routing";
import { BoardChangeEvent, IssueCard, IssuePage, IssuePriority, IssueType } from "./types";

// Board filters applied to a column query (statusId + cursor are managed by the hook).
export interface ColumnFilters {
  q?: string;
  jql?: string;
  assignee?: string | null;
  priority?: IssuePriority | null;
  type?: IssueType | null;
}

export interface ColumnIssuesState {
  items: IssueCard[];
  total: number;
  loading: boolean;
  // True only while the NEXT page is being fetched (infinite scroll), unlike `loading` which is true
  // for any in-flight fetch (initial load, refetch). Drives the slim "loading more" indicator.
  loadingMore: boolean;
  hasMore: boolean;
  hasLoaded: boolean;
  error: boolean;
  loadMore: () => void;
  reload: () => void;
  // Optimistically append a just-created card so it shows instantly; the next reload
  // (BoardService emit after the create resolves) replaces it with server truth.
  addOptimistic: (card: IssueCard) => void;
  // Optimistic drag-move primitives — remove a card by id / insert a card at an index — so a
  // dropped card stays put instead of snapping back until the move's reload lands.
  removeOptimistic: (id: string) => void;
  insertOptimistic: (card: IssueCard, index: number) => void;
  // Patch a loaded card's fields in place (e.g. assignee/priority) so a context action
  // reflects instantly; the reload reconciles to server truth.
  patchOptimistic: (id: string, patch: Partial<IssueCard>) => void;
}

const PAGE_SIZE: number = 50;

// The cached shape of a column: TanStack's paged container of IssuePage results.
type ColumnData = InfiniteData<IssuePage>;

// ── Pure helpers over the paged cache ────────────────────────────────────────
// All return a new ColumnData (immutable) so setQueryData updates re-render cleanly. `total`
// is read from the first page, so optimistic count changes adjust page 0's total.

function flattenItems(data: ColumnData | undefined): IssueCard[] {
  if (!data) return [];
  return data.pages.flatMap((page: IssuePage) => page.items);
}

function bumpFirstPageTotal(pages: IssuePage[], delta: number): IssuePage[] {
  if (pages.length === 0) return pages;
  return pages.map((page: IssuePage, index: number) =>
    index === 0 ? { ...page, total: Math.max(0, page.total + delta) } : page
  );
}

function appendCard(data: ColumnData, card: IssueCard): ColumnData {
  const lastIndex: number = data.pages.length - 1;
  const withCard: IssuePage[] = data.pages.map((page: IssuePage, index: number) =>
    index === lastIndex ? { ...page, items: [...page.items, card] } : page
  );
  return { ...data, pages: bumpFirstPageTotal(withCard, 1) };
}

function removeCard(data: ColumnData, id: string): ColumnData {
  const existed: boolean = data.pages.some((page: IssuePage) => page.items.some((card: IssueCard) => card.id === id));
  const filtered: IssuePage[] = data.pages.map((page: IssuePage) => ({
    ...page,
    items: page.items.filter((card: IssueCard) => card.id !== id)
  }));
  return { ...data, pages: existed ? bumpFirstPageTotal(filtered, -1) : filtered };
}

function insertCard(data: ColumnData, card: IssueCard, index: number): ColumnData {
  let consumed: number = 0;
  let inserted: boolean = false;
  const pages: IssuePage[] = data.pages.map((page: IssuePage) => {
    const length: number = page.items.length;
    if (!inserted && index <= consumed + length) {
      const local: number = Math.max(0, index - consumed);
      const items: IssueCard[] = page.items.slice();
      items.splice(local, 0, card);
      inserted = true;
      consumed += length;
      return { ...page, items };
    }
    consumed += length;
    return page;
  });
  if (!inserted && pages.length > 0) {
    const lastIndex: number = pages.length - 1;
    pages[lastIndex] = { ...pages[lastIndex], items: [...pages[lastIndex].items, card] };
  }
  return { ...data, pages: bumpFirstPageTotal(pages, 1) };
}

function patchCard(data: ColumnData, id: string, patch: Partial<IssueCard>): ColumnData {
  return {
    ...data,
    pages: data.pages.map((page: IssuePage) => ({
      ...page,
      items: page.items.map((card: IssueCard) => (card.id === id ? { ...card, ...patch } : card))
    }))
  };
}

// Cursor-paginated, infinite-scroll loader for one board column, backed by TanStack Query's
// useInfiniteQuery. The query key (board + status + filters) owns identity, so a filter change
// transparently starts a fresh page-1 query, while invalidation refetches exactly the pages
// already loaded — preserving scroll depth for free. Optimistic edits mutate the cached pages
// directly; the event-driven invalidation reconciles to server truth.
// Options for the column query. `enabled` (default true) gates the network: the local-first
// path (use-column-source) sets it false when the in-memory store serves the column, so the
// per-column server query never fires.
export interface UseColumnIssuesOptions {
  enabled?: boolean;
}

export function useColumnIssues(
  boardId: string,
  columnId: string,
  statusIds: string[],
  filters: ColumnFilters,
  options?: UseColumnIssuesOptions
): ColumnIssuesState {
  const queryClient: QueryClient = useQueryClient();
  const filterKey: string = JSON.stringify(filters);
  const filtersRef = useRef<ColumnFilters>(filters);
  filtersRef.current = filters;
  // Stable key for the column's status set so the query refetches / the event closure refreshes
  // when the column's statuses are reassigned.
  const statusIdsKey: string = statusIds.join(",");
  const statusIdsRef = useRef<string[]>(statusIds);
  statusIdsRef.current = statusIds;
  const enabled: boolean = options?.enabled ?? true;

  const query = useInfiniteQuery({
    queryKey: boardKeys.column(boardId, columnId, filters),
    enabled,
    queryFn: ({ pageParam = null }: { pageParam?: string | null }): Promise<IssuePage> => {
      const f: ColumnFilters = filtersRef.current;
      return BoardApi.queryIssues(boardId, {
        statusIds: statusIdsRef.current,
        q: f.q,
        jql: f.jql,
        assignee: f.assignee,
        priority: f.priority,
        type: f.type,
        cursor: pageParam,
        limit: PAGE_SIZE
      });
    },
    getNextPageParam: (lastPage: IssuePage): string | undefined => lastPage.nextCursor ?? undefined
  });

  // Memoize on the (stable across renders) query.data reference so `items` keeps a stable
  // identity when the data hasn't changed. Without this, flattenItems would return a fresh array
  // every render, and consumers that report `items` up on change (BoardColumn → onData) would
  // loop: report → parent setState → re-render → new array → report → … ("Maximum update depth").
  const items: IssueCard[] = useMemo(() => flattenItems(query.data), [query.data]);
  const total: number = query.data?.pages[0]?.total ?? 0;

  // Live mirror of the flattened items for the event handler's membership test, without
  // re-subscribing on every list change.
  const itemsRef = useRef<IssueCard[]>(items);
  itemsRef.current = items;

  const loadMore = useCallback((): void => {
    if (!query.isFetchingNextPage && query.hasNextPage) void query.fetchNextPage();
  }, [query]);

  const reload = useCallback((): void => {
    void query.refetch();
  }, [query]);

  const setColumn = useCallback(
    (updater: (prev: ColumnData) => ColumnData): void => {
      queryClient.setQueryData<ColumnData>(boardKeys.column(boardId, columnId, filtersRef.current), (prev) =>
        prev ? updater(prev) : prev
      );
    },
    [queryClient, boardId, columnId]
  );

  const addOptimistic = useCallback(
    (card: IssueCard): void => setColumn((prev) => appendCard(prev, card)),
    [setColumn]
  );
  const removeOptimistic = useCallback((id: string): void => setColumn((prev) => removeCard(prev, id)), [setColumn]);
  const insertOptimistic = useCallback(
    (card: IssueCard, index: number): void => setColumn((prev) => insertCard(prev, card, index)),
    [setColumn]
  );
  const patchOptimistic = useCallback(
    (id: string, patch: Partial<IssueCard>): void => setColumn((prev) => patchCard(prev, id, patch)),
    [setColumn]
  );

  // Board mutations (local and teammates' over SSE) route through BoardService, which emits a
  // typed event. React only when the event touches THIS column: a same-column field edit patches
  // the held card in place (no refetch); otherwise invalidate this column's cached pages so
  // useInfiniteQuery refetches exactly the depth currently shown.
  useEffect(() => {
    const unsubscribe: () => void = BoardService.subscribe((event: BoardChangeEvent) => {
      const issueId: string | undefined = "issueId" in event ? event.issueId : undefined;
      const containsIssue: boolean =
        issueId !== undefined && itemsRef.current.some((card: IssueCard) => card.id === issueId);
      if (
        event.kind === "issue.updated" &&
        event.patch !== undefined &&
        issueId !== undefined &&
        containsIssue &&
        // Authoritative events carry the (server) statusId → patch only when that status belongs
        // to this column. Optimistic events carry no statusId and never change the column (statusId
        // edits opt out), so the column that holds the card is always the right one to patch.
        (event.statusId === undefined || statusIdsRef.current.includes(event.statusId))
      ) {
        const patch: Partial<IssueCard> = event.patch;
        setColumn((prev) => patchCard(prev, issueId, patch));
        return;
      }
      if (!columnShouldReload(event, statusIdsRef.current, containsIssue)) return;
      void queryClient.invalidateQueries({ queryKey: ["board", boardId, "column", columnId] });
    });
    return unsubscribe;
    // filterKey + statusIdsKey are included so the membership/closure tracks the active filter set
    // and the column's status mapping.
  }, [queryClient, setColumn, boardId, columnId, filterKey, statusIdsKey]);

  return {
    items,
    total,
    loading: query.isFetching,
    loadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage ?? false,
    hasLoaded: query.isSuccess,
    error: query.isError,
    loadMore,
    reload,
    addOptimistic,
    removeOptimistic,
    insertOptimistic,
    patchOptimistic
  };
}
